/**
 * OwnerBalanceCard — hero card yang tampil di atas Dashboard kalau role=owner.
 * Fetch saldo + ringkasan (Total Deposit / Total Penggunaan) dari endpoint owner API.
 * Klik card → navigasi ke /app/my-deposits untuk detail history.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Wallet, TrendingUp, TrendingDown, ArrowUpRight, RefreshCw, AlertTriangle, Plus } from 'lucide-react';
import OwnerTopUpModal from './OwnerTopUpModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtIDR = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

const OwnerBalanceCard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [balance, setBalance] = useState(0);
  const [feePct, setFeePct] = useState(0);
  const [ownerName, setOwnerName] = useState('');
  const [ownerCode, setOwnerCode] = useState('');
  const [totalDeposit, setTotalDeposit] = useState(0);
  const [totalUsage, setTotalUsage] = useState(0);
  const [depositCount, setDepositCount] = useState(0);
  const [error, setError] = useState(null);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const fetchData = async (showSpinner = true) => {
    if (showSpinner) setLoading(true); else setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const hdr = { headers: { Authorization: `Bearer ${token}` } };
      const [balRes, depRes] = await Promise.all([
        axios.get(`${API_URL}/api/owner/my-balance`, hdr),
        axios.get(`${API_URL}/api/owner/my-deposits`, hdr),
      ]);
      if (balRes.data?.success && balRes.data.data) {
        setBalance(Number(balRes.data.data.balance) || 0);
        setFeePct(Number(balRes.data.data.fee_percentage) || 0);
        setOwnerName(balRes.data.data.owner_name || '');
        setOwnerCode(balRes.data.data.owner_code || '');
      }
      if (depRes.data?.success && Array.isArray(depRes.data.data)) {
        const all = depRes.data.data;
        const topups = all
          .filter(h => h.type === 'topup' || (h.type === 'adjustment' && Number(h.amount) > 0))
          .map(h => Math.abs(Number(h.amount) || 0));
        const usages = all
          .filter(h => h.type === 'usage' || (h.type === 'adjustment' && Number(h.amount) < 0))
          .map(h => Math.abs(Number(h.amount) || 0));
        setTotalDeposit(topups.reduce((s, a) => s + a, 0));
        setTotalUsage(usages.reduce((s, a) => s + a, 0));
        setDepositCount(topups.length);
      }
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Gagal memuat saldo');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(true); /* eslint-disable-next-line */ }, []);

  const lowBalance = !loading && balance >= 0 && balance < 50000;

  if (loading) {
    return (
      <div className="rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-6 shadow-lg animate-pulse">
        <div className="h-4 w-32 bg-white/30 rounded mb-3"></div>
        <div className="h-10 w-56 bg-white/40 rounded"></div>
      </div>
    );
  }

  return (
    <div
      data-testid="owner-balance-card"
      className="relative rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-6 shadow-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow"
      onClick={() => navigate('/app/my-deposits')}
      role="button"
      tabIndex={0}
    >
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute -top-4 -right-4 h-32 w-32 rounded-full bg-white blur-2xl"></div>
        <div className="absolute -bottom-6 -left-6 h-24 w-24 rounded-full bg-yellow-200 blur-2xl"></div>
      </div>

      <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-wider mb-1">
            <Wallet size={14} />
            <span>Saldo Deposit</span>
            {ownerCode && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/20 text-white text-[10px] font-medium">{ownerCode}</span>}
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="text-white text-3xl sm:text-4xl font-bold tracking-tight" data-testid="owner-balance-amount">
              {fmtIDR(balance)}
            </div>
            {feePct > 0 && (
              <div className="text-white/80 text-xs">Fee {feePct}%</div>
            )}
          </div>
          {ownerName && <div className="mt-1 text-white/80 text-sm">{ownerName}</div>}
          {lowBalance && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-400/90 text-amber-950 text-[11px] font-semibold">
              <AlertTriangle size={12} /> Saldo rendah — segera top up
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 sm:gap-5 flex-wrap">
          <div className="text-white/95">
            <div className="flex items-center gap-1 text-white/70 text-[11px] uppercase">
              <TrendingUp size={12} /> Total Deposit
            </div>
            <div className="text-lg font-semibold" data-testid="owner-total-deposit">{fmtIDR(totalDeposit)}</div>
            <div className="text-[10px] text-white/60">{depositCount}x transaksi</div>
          </div>
          <div className="text-white/95">
            <div className="flex items-center gap-1 text-white/70 text-[11px] uppercase">
              <TrendingDown size={12} /> Total Pakai
            </div>
            <div className="text-lg font-semibold" data-testid="owner-total-usage">{fmtIDR(totalUsage)}</div>
          </div>
          <div className="flex items-center gap-2 ml-auto sm:ml-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setTopUpOpen(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-400 text-emerald-950 text-xs font-semibold hover:bg-emerald-300 transition-colors shadow-sm"
              data-testid="owner-balance-topup-btn"
            >
              <Plus size={12} /> Top Up
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); fetchData(false); }}
              disabled={refreshing}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 disabled:opacity-50 transition-colors"
              title="Refresh saldo"
              data-testid="owner-balance-refresh-btn"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <div
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-indigo-700 text-xs font-semibold shadow-sm"
              data-testid="owner-balance-view-detail"
            >
              Detail <ArrowUpRight size={12} />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="relative z-10 mt-3 text-xs text-white/90 bg-red-500/40 rounded px-2 py-1 inline-block">
          {error}
        </div>
      )}
      <OwnerTopUpModal
        isOpen={topUpOpen}
        onClose={() => { setTopUpOpen(false); fetchData(false); }}
        ownerName={ownerName}
        currentBalance={balance}
      />
    </div>
  );
};

export default OwnerBalanceCard;
